import React, { createRef, useEffect, useState } from 'react';
import './ImageCroppingTool.scss';
import Dropzone, { DropzoneRef } from 'react-dropzone';
import createNotification from '../../utils/createNotification';
import uploadArrow from '../../assets/images/uploadArrow.png';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { faMinus, faPlus } from '@fortawesome/fontawesome-free-solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ImageCroppingToolProps {
    initialAspect?: number;
    selectedFile: File;
    setSelectedFile(file: File): void;
    close(): void;
    setCroppedFile?(file: File): void;
    setCroppedImageUrl?(image: string): void;
    preSelectedFile?: File;
}

export const ImageCroppingTool = (props: ImageCroppingToolProps) => {
    const dropzoneRef = createRef<DropzoneRef>();
    const cropperRef = createRef<ReactCropperElement>();
    const [selectedImage, setSelectedImage] = useState<HTMLImageElement>(null);
    const allowedImageExtensions = ['.jpg', '.jpeg', '.png', '.gif'];
    const maxFileSize = 5 * 1024 * 1024;

    useEffect(() => {
        if (props.preSelectedFile) {
            addFile(props.preSelectedFile);
        }
    }, [props.preSelectedFile]);

    const addFile = (newFile: File) => {
        if (!newFile) {
            return;
        }
        if (
            allowedImageExtensions.every(
                (e) => !newFile.name.toLowerCase().endsWith(e)
            )
        ) {
            createNotification(
                'You are allowed to upload only jpg, jpeg, png or gif files.',
                'error'
            );
            return;
        }

        if (newFile.size > maxFileSize) {
            createNotification('Max file size is 5 Mb.', 'error');
            return;
        }

        const url = URL.createObjectURL(newFile);
        const selectedImage = new Image();
        selectedImage.src = url;
        selectedImage.onerror = () => {
            setSelectedImage(null);
            createNotification(
                'The file cannot be read, please try other one.',
                'error'
            );
        };
        selectedImage.onload = () => {
            setSelectedImage(selectedImage);
            props.setSelectedFile(newFile);
        };
    };

    const openUploadDialog = () => {
        if (dropzoneRef.current) {
            dropzoneRef.current.open();
        }
    };

    const isCroppingToolVisible = () => selectedImage && props.selectedFile;

    const getCropData = () => {
        if (typeof cropperRef.current?.cropper !== 'undefined') {
            const cropUrl = cropperRef.current?.cropper
                .getCroppedCanvas()
                .toDataURL();
            return cropUrl;
        }
    };

    const applyCropping = (url: string) => {
        fetch(url)
            .then((res) => res.blob())
            .then((blob) => {
                const croppedFile = new File([blob], props.selectedFile.name, {
                    type: props.selectedFile.type,
                });
                props.setCroppedFile?.(croppedFile);
                props.setCroppedImageUrl?.(url);
            });
    };
    const zoomIn = () => {
        if (cropperRef.current) {
            cropperRef.current.cropper.zoom(0.1);
        }
    };

    const zoomOut = () => {
        if (cropperRef.current) {
            cropperRef.current.cropper.zoom(-0.1);
        }
    };

    return (
        <div className="image-cropping-tool">
            {isCroppingToolVisible() ? (
                <div className="cropper-tool-container">
                    <div className="cropper-wrapper">
                        <div className="zoom-buttons">
                            <button className="zoom-btn" onClick={zoomIn}>
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={faPlus as any}
                                />
                            </button>
                            <button className="zoom-btn" onClick={zoomOut}>
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={faMinus as any}
                                />
                            </button>
                        </div>
                        <Cropper
                            zoomable={true}
                            ref={cropperRef}
                            style={{ height: 400, width: '100%' }}
                            initialAspectRatio={props.initialAspect ?? 1}
                            preview=".img-preview"
                            src={selectedImage.src}
                            viewMode={1}
                            minCropBoxHeight={10}
                            minCropBoxWidth={10}
                            background={false}
                            responsive={true}
                            autoCropArea={1}
                            checkOrientation={false}
                            guides={true}
                            restore={false}
                        />
                    </div>

                    <div className="buttons-wrapper">
                        <button
                            className="cancel-btn"
                            onClick={() => props.close()}>
                            Cancel
                        </button>
                        <button
                            className="reset-btn"
                            onClick={() => props.setSelectedFile(null)}>
                            Reset Image
                        </button>
                        <button
                            className="btn apply-btn"
                            onClick={() => {
                                const result = getCropData();
                                applyCropping(result);
                            }}>
                            Apply
                        </button>
                    </div>
                </div>
            ) : (
                <Dropzone
                    noClick
                    noKeyboard
                    ref={dropzoneRef}
                    onDrop={(acceptedFiles: File[]) =>
                        addFile(acceptedFiles[0])
                    }
                    accept={{ 'image/jpeg': allowedImageExtensions }}>
                    {({ getRootProps, getInputProps }) => {
                        return (
                            <div>
                                <div
                                    {...getRootProps({
                                        className: 'dropzone',
                                    })}>
                                    <input
                                        {...getInputProps()}
                                        accept={allowedImageExtensions.join(
                                            ','
                                        )}
                                    />
                                    <div>
                                        <img src={uploadArrow} />
                                    </div>
                                    <p
                                        className="drop-text"
                                        style={{
                                            fontSize: '13px',
                                            color: '#888f96',
                                        }}>
                                        Drop Files or Click to Upload
                                    </p>
                                    <span
                                        className="or-option"
                                        style={{
                                            fontSize: '13px',
                                            color: '#888f96',
                                        }}>
                                        ------ or -------
                                    </span>
                                    <button
                                        type="button"
                                        onClick={openUploadDialog}
                                        className="browse-btn">
                                        Browse Files
                                    </button>
                                </div>
                            </div>
                        );
                    }}
                </Dropzone>
            )}
        </div>
    );
};

export default ImageCroppingTool;
